import { useThree } from '@react-three/fiber';
import { useEffect, useMemo, type RefObject } from 'react';
import {
  color,
  cos,
  deltaTime,
  distance,
  float,
  Fn,
  hash,
  If,
  instancedArray,
  instanceIndex,
  mat3,
  max,
  min,
  mix,
  positionLocal,
  range,
  select,
  sin,
  step,
  time,
  uint,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';

import * as THREE from 'three/webgpu';
import { curlNoise4d } from './noise-functions/curl-noise-4d';

interface ParticleSystemTypes {
  count: number;
  emitterObjects: RefObject<THREE.Object3D<THREE.Object3DEventMap>>;
}

export const useParticleSystem = ({
  count = 1000,
  emitterObjects,
}: ParticleSystemTypes) => {
  const gl = useThree((state) => state.gl as unknown as THREE.WebGPURenderer);

  /**------------
     * Emittter
     --------------*/
  const emitter = useMemo(() => {
    return {
      emitterPosition: new THREE.Vector3(1, 1, 0),
      velocity: new THREE.Vector3(),
    };
  }, []);
  const { shaderNodes, computeNode, uniforms, buffers } = useMemo(() => {
    /**----------------
      Buffer Arrays
     * ---------------*/
    const positions = instancedArray(count, 'vec3');
    const velocities = instancedArray(count, 'vec3');
    const lifeBuffer = instancedArray(count, 'float');
    const rotationBuffer = instancedArray(count, 'vec3');
    /**----------------
     * Unifoms
     * ---------------*/
    const turbulencePositionFrequency = uniform(3, 'float');
    const turbulenceTimeFrequency = uniform(0.1, 'float');
    const turbulenceStrength = uniform(0.01, 'float');
    const gravity = uniform(vec3(0, -0.5, 0), 'vec3');
    const floorY = uniform(-0.95, 'float');
    const floorDamping = uniform(0.1, 'float');
    const decayFrequency = uniform(0.2, 'float');
    const emitterPosition = uniform(vec3(0), 'vec3');
    const emitterPreviousPosition = uniform(vec3(0), 'vec3');
    const emitterVelocity = uniform(vec3(0), 'vec3');
    const emitterPreviousVelocity = uniform(vec3(0), 'vec3');
    const emitterRadius = uniform(0.01, 'float');
    const velocityDamping = uniform(0.01, 'float');
    const emitterVelocityStrength = uniform(0.4, 'float');
    const initialRandomVelocity = uniform(0, 'float');
    const initialVelocity = uniform(vec3(0, 0, 0));
    const fadeIn = uniform(0.2, 'float');
    const fadeOut = uniform(0.2, 'float');
    const size = uniform(0.075, 'float');
    const sparklingFrequency = uniform(1.0, 'float');
    const sparklingDuration = uniform(0.01, 'float');
    const sparklingAlpha = uniform(4.0, 'float');
    const solidRatio = uniform(0.05, 'float');
    const solidAlpha = uniform(0.05, 'float');
    const glowSpread = uniform(0.02, 'float');
    const opacity = uniform(1.0, 'float');
    const colorIn = uniform(color('#aa5100'));
    const colorOut = uniform(color('#0345aa'));
    const scaleRangeStart = uniform(0.5, 'float');
    const scaleRangeEnd = uniform(1, 'float');

    // const rotation = uniform(vec3(), "vec3")

    /**----------------*
     * Varying
     * ---------------*/
    const sparkling = varying(float(0));

    /**----------------*
     * compute Node
     * ---------------*/

    //Initialize GPU computation
    const computeInit = Fn(() => {
      const position = positions.element(instanceIndex); //get each instance particle posiiton
      const life = lifeBuffer.element(instanceIndex); //get each instance particle life

      position.assign(vec3(99999)); //send the particles offscreen

      life.assign(hash(instanceIndex)); //assign a randome life number to each particle instance
    })().compute(count);
    gl.computeAsync(computeInit); //send to the webgpu renderer

    //Update compute
    const computeUpdate = Fn(() => {
      const position = positions.element(instanceIndex);
      const velocity = velocities.element(instanceIndex);
      const life = lifeBuffer.element(instanceIndex);

      const delta = deltaTime;

      //create turbulence with a noise function
      const turbulenceInput = position
        .mul(turbulencePositionFrequency)
        .add(12.34);
      const turbulence = curlNoise4d(
        vec4(turbulenceInput, time.mul(turbulenceTimeFrequency))
      ).mul(turbulenceStrength);

      //update velocity
      velocity.addAssign(turbulence);
      velocity.addAssign(gravity.mul(delta));
      velocity.mulAssign(velocityDamping.oneMinus());

      //update position
      position.addAssign(velocity.mul(delta));

      const rotation = rotationBuffer.element(instanceIndex);

      const rotationSpeed = vec3(
        hash(instanceIndex.mul(12.9898)).mul(4.0).sub(2.0), // -2 to 2
        hash(instanceIndex.mul(78.233)).mul(4.0).sub(2.0),
        hash(instanceIndex.mul(43.758)).mul(4.0).sub(2.0)
      );

      // Accumulate rotation over time
      rotation.addAssign(rotationSpeed.mul(delta));

      //floor bounce
      If(position.y.lessThan(floorY), () => {
        position.y.assign(floorY);
        velocity.y.mulAssign(floorDamping.oneMinus().negate());
      });

      //life
      const newLife = life.add(delta.mul(decayFrequency));

      //rest
      If(newLife.greaterThan(1), () => {
        const randomDirection = vec3(
          hash(instanceIndex.add(uint(Math.random() * 0xffffff))).sub(0.5),
          hash(instanceIndex.add(uint(Math.random() * 0xffffff))).sub(0.5),
          hash(instanceIndex.add(uint(Math.random() * 0xffffff))).sub(0.5)
        ).normalize();

        //create random strength value based on the instance index of the particule
        const mixStrength = hash(
          instanceIndex.add(uint(Math.random() * 0xffffff))
        );

        //position
        const newPosition = mix(
          emitterPosition,
          emitterPreviousPosition,
          mixStrength
        );

        newPosition.addAssign(randomDirection.mul(emitterRadius));

        //update the instances position
        position.assign(newPosition);

        //velocity
        const newVelocity = mix(
          emitterVelocity,
          emitterPreviousVelocity,
          mixStrength
        );

        velocity.assign(
          newVelocity
            .mul(emitterVelocityStrength)
            .add(randomDirection.mul(initialRandomVelocity))
            .add(initialVelocity)
        );
      });

      life.assign(newLife.mod(1));
    })().compute(count);

    const life = lifeBuffer.toAttribute();
    /**----------*
      Scale property
    *------------*/
    const scaleIn = life.remap(0, fadeIn, 0, 1);
    const scaleOut = life.remap(fadeOut.oneMinus(), 1, 1, 0);
    const scaleFinal = min(scaleIn, scaleOut)
      .smoothstep(0, 1)
      .mul(size)
      .mul(range(scaleRangeStart, scaleRangeEnd));

    /**----------------*
     * Position Node
     * ---------------*/
    const posNode = Fn(() => {
      //calculates sparking varying in vertex node
      const sparklingTime = hash(
        instanceIndex.add(uint(Math.random() * 0xffffff))
      );

      const sparklingLife = life.mul(sparklingFrequency).mod(1);

      //update sparkling varying
      // Either 0 or 1 based on
      // (sparklingLife < sparklingTime) & (sparklingLife >sparklingTime - sparklingDuration * sparklingFrequency)
      sparkling.assign(
        select(
          sparklingLife
            .lessThan(sparklingTime)
            .and(
              sparklingLife.greaterThan(
                sparklingTime.sub(sparklingDuration.mul(sparklingFrequency))
              )
            ),
          1,
          0
        )
      );

      const rotation = rotationBuffer.toAttribute();

      const cosX = cos(rotation.x);
      const sinX = sin(rotation.x);
      const cosY = cos(rotation.y);
      const sinY = sin(rotation.y);
      const cosZ = cos(rotation.z);
      const sinZ = sin(rotation.z);

      // Build rotation matrix
      const rotMatrix = mat3(
        cosY.mul(cosZ),
        cosY.mul(sinZ),
        sinY.negate(),
        sinX.mul(sinY).mul(cosZ).sub(cosX.mul(sinZ)),
        sinX.mul(sinY).mul(sinZ).add(cosX.mul(cosZ)),
        sinX.mul(cosY),
        cosX.mul(sinY).mul(cosZ).add(sinX.mul(sinZ)),
        cosX.mul(sinY).mul(sinZ).sub(sinX.mul(cosZ)),
        cosX.mul(cosY)
      );

      //return this alone if you wants the position alone
      const basePosition = positions.toAttribute();

      // Get local vertex position (from the sprite/plane geometry)
      const localPos = positionLocal;

      // Rotate the local vertex position
      const rotatedLocal = rotMatrix.mul(localPos);

      // Add rotated local position to world position
      return basePosition.add(rotatedLocal);
    })();

    /**----------------*
     * Color Node
     * ---------------*/
    const colorNode = Fn(() => {
      const distanceToCenter = distance(uv(), vec2(0.5));

      const alphaSolid = step(solidRatio.div(2), distanceToCenter)
        .oneMinus()
        .mul(solidAlpha);

      const alphaGlow = glowSpread.div(distanceToCenter).sub(glowSpread.mul(2));
      alphaGlow.mulAssign(alphaSolid.oneMinus());

      const alphaFinal = max(alphaGlow, alphaSolid).mul(opacity);

      alphaFinal.mulAssign(sparkling.mul(sparklingAlpha).add(1));

      const finaColor = mix(colorIn, colorOut, life);

      return vec4(finaColor, alphaFinal);
    })();

    /**----------*
      Scale Node
    *------------*/
    const scaleNode = Fn(() => {
      return vec2(scaleFinal);
    })();

    return {
      shaderNodes: {
        posNode,
        colorNode,
        scaleNode,
      },
      computeNode: {
        computeInit,
        computeUpdate,
      },
      uniforms: {
        turbulencePositionFrequency,
        turbulenceTimeFrequency,
        turbulenceStrength,
        gravity,
        floorY,
        floorDamping,
        decayFrequency,
        emitterPosition,
        emitterPreviousPosition,
        emitterVelocity,
        emitterPreviousVelocity,
        emitterRadius,
        velocityDamping,
        emitterVelocityStrength,
        initialRandomVelocity,
        initialVelocity,
        fadeIn,
        fadeOut,
        size,
        scaleRangeStart,
        scaleRangeEnd,
        sparklingFrequency,
        sparklingDuration,
        sparklingAlpha,
        solidRatio,
        solidAlpha,
        glowSpread,
        opacity,
        colorIn,
        colorOut,
      },
      buffers: {
        positions,
        velocities,
        lifeBuffer,
        rotationBuffer,
      },
    };
  }, [gl, count]);

  /**
   * send this to the use frame function
   * @param delta - The delta time
   */
  const update = (delta: number) => {
    //compute the particles on every frame
    gl.computeAsync(computeNode.computeUpdate);

    emitter.emitterPosition.copy(emitterObjects.current.position);
    uniforms.emitterVelocity.value.copy(emitter.velocity);

    // //update velocity on each frame
    const velocity = emitter.emitterPosition
      .clone()
      .sub(uniforms.emitterPreviousPosition.value)
      .divideScalar(delta);
    uniforms.emitterVelocity.value.copy(velocity);

    //update the position on the emiter on each frame
    uniforms.emitterPosition.value.copy(emitter.emitterPosition);

    ///update previous values on each frame
    uniforms.emitterPreviousPosition.value.copy(uniforms.emitterPosition.value);
    uniforms.emitterPreviousVelocity.value.copy(uniforms.emitterVelocity.value);
  };
  //dispose the buffers and compute functions when the component unmounts
  useEffect(() => {
    return () => {
      buffers.lifeBuffer.dispose();
      buffers.positions.dispose();
      buffers.velocities.dispose();
      buffers.rotationBuffer.dispose();

      computeNode.computeInit.dispose();
      computeNode.computeUpdate.dispose();
    };
  }, [buffers, computeNode]);

  return { shaderNodes, uniforms, update };
};
